import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {useLocation} from 'react-router-native';
import {
  // Components
  Link,
  Text,
  View,
  // Helpers
  SIZE,
  COLOR,
} from '@lookiero/aurora';

import {useToast, generateUid, useOverallInfo, useFeatureFlags} from '@components/_context';
import {ModalReturnInstructions, ModalInboundInstructions} from '@components/atoms';
import {INBOUND_INSTRUCTIONS_FEATURE_FLAG_PREFIX} from '@components/atoms/ModalInboundInstructions/ModalInboundInstructions.definition';
import {OrderService} from '@domain/model/Order';
import {RETURN_INSTRUCTIONS_PARAM, TEXT} from './NotificationReturnInstructions.definition';

interface NotificationReturnInstructionsProps {
  readonly orderPendingReturn: OrderService | undefined;
}

const NotificationReturnInstructions: React.FC<NotificationReturnInstructionsProps> = ({orderPendingReturn}) => {
  const {formatMessage} = useIntl();
  const {search} = useLocation();
  const {country = ''} = useOverallInfo();
  const featureFlags = useFeatureFlags();
  const uid = useMemo<string>(() => generateUid(), []);
  const toast = useToast(uid);
  const [modalInstructions, setModalInstructions] = useState<boolean>(false);

  const featureFlagsLoaded = Object.keys(featureFlags).length > 0;
  const inboundInstructionsFeatureFlag = `${INBOUND_INSTRUCTIONS_FEATURE_FLAG_PREFIX}_${country}`;
  const useInboundInstructions = Boolean(country && featureFlags[inboundInstructionsFeatureFlag]);

  const onOpenInstructionsModal = useCallback(() => {
    setModalInstructions(true);
    toast.hide(uid);
  }, [toast, uid]);
  const onCloseInstructionsModal = useCallback(() => setModalInstructions(false), []);

  useEffect(() => {
    if (orderPendingReturn) {
      toast.showInformation({
        message: (
          <>
            <View wide>
              <Text detail level={2} color={COLOR.BG_BASE}>
                {formatMessage(TEXT.ACTION_RETURN_TITLE)}
              </Text>
            </View>
            <Link detail level={2} onPress={onOpenInstructionsModal} underlined color={COLOR.BG_BASE}>
              {formatMessage(TEXT.ACTION_RETURN_LINK)}
            </Link>
          </>
        ),
        settings: {
          timeoutClose: 0,
        },
      });
      return () => {
        toast.hide(uid);
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (orderPendingReturn && new URLSearchParams(search).has(RETURN_INSTRUCTIONS_PARAM)) {
      onOpenInstructionsModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!orderPendingReturn || !modalInstructions || !featureFlagsLoaded) {
    return null;
  }

  return useInboundInstructions ? (
    <ModalInboundInstructions
      orderReference={orderPendingReturn.id}
      orderId={undefined}
      layer={SIZE.XL}
      onClose={onCloseInstructionsModal}
      isVisible={true}
    />
  ) : (
    <ModalReturnInstructions
      orderReference={`LK-${orderPendingReturn.id}`}
      serviceId={undefined}
      layer={SIZE.XL}
      onClose={onCloseInstructionsModal}
      isVisible={true}
    />
  );
};

export {NotificationReturnInstructions};
